import { useParams, Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FlaskConical, Activity, Settings2, GitBranch } from "lucide-react";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { useSkillById } from "@/hooks/useSkillDetail";
import { IntentTab } from "@/components/skills/detail/IntentTab";
import { GoldenTab } from "@/components/skills/detail/GoldenTab";
import { ObservabilityTab } from "@/components/skills/detail/ObservabilityTab";
import { RolloutTab } from "@/components/skills/detail/RolloutTab";

export default function SkillDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: skill, isLoading } = useSkillById(id);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!skill) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Skill não encontrada.
          <div className="mt-3">
            <Link to="/skills">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Breadcrumb items={[{ label: "Skills", to: "/skills" }, { label: skill.name }]} />
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-headline">{skill.name}</h1>
          <p className="text-sm text-muted-foreground">{skill.description || "—"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{skill.execution_type}</Badge>
          <Badge>{skill.rollout_state}</Badge>
          <Badge variant="outline">v{skill.version}</Badge>
        </div>
      </div>

      <Tabs defaultValue="intent">
        <TabsList>
          <TabsTrigger value="intent">
            <Settings2 className="w-3 h-3 mr-1" /> Intenção
          </TabsTrigger>
          <TabsTrigger value="golden">
            <FlaskConical className="w-3 h-3 mr-1" /> Teste
          </TabsTrigger>
          <TabsTrigger value="observability">
            <Activity className="w-3 h-3 mr-1" /> Observabilidade
          </TabsTrigger>
          <TabsTrigger value="rollout">
            <GitBranch className="w-3 h-3 mr-1" /> Rollout
          </TabsTrigger>
        </TabsList>
        <TabsContent value="intent" className="mt-4">
          <IntentTab skill={skill} />
        </TabsContent>
        <TabsContent value="golden" className="mt-4">
          <GoldenTab skill={skill} />
        </TabsContent>
        <TabsContent value="observability" className="mt-4">
          <ObservabilityTab skill={skill} />
        </TabsContent>
        <TabsContent value="rollout" className="mt-4">
          <RolloutTab skill={skill} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
